import { GlobalContext } from 'api/GlobalContext';
import React, { useState, useEffect, useContext } from 'react';
import { convertAccessLevelForBackend } from './utils';
import { fetchUserSharedConfigsDetails } from './apiUtils';
import {
  Button,
  Input,
  FormGroup,
  Label,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';

const ShareTemplateModal = ({
  isOpen,
  onClose,
  configID,
  sharedAccessData,
}) => {
  const { apiClient } = useContext(GlobalContext);
  const [shares, setShares] = useState([{ email: '', access: 'View Only' }]);
  const [dropdownOpen, setDropdownOpen] = useState(
    new Array(shares.length).fill(false),
  );
  const [groupShare, setGroupShare] = useState(false);
  const [checkboxDropdownOpen, setCheckboxDropdownOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState(null);
  const [sharingInProgress, setSharingInProgress] = useState(false);
  const [lastDropdownValue, setLastDropdownValue] = useState('View Only'); // State for the last dropdown value
  const [labelText, setLabelText] = useState('');

  useEffect(() => {
    apiClient
      .getStats({})
      .then((data) => {
        setUserEmail(data.email);
      })
      .catch((error) => {
        console.error('Error fetching user details:', error);
      });
  }, [apiClient]);

  useEffect(() => {
    if (sharedAccessData && sharedAccessData.length > 0) {
      setShares(sharedAccessData);
    }
  }, [sharedAccessData]);

  if (!isOpen) return null;

  const handleShareChange = (index, value, field) => {
    const updatedShares = shares.map((share, i) => {
      if (i === index) {
        return { ...share, [field]: value };
      }
      return share;
    });
    setShares(updatedShares);
  };

  const addShare = () => {
    setShares([...shares, { email: '', access: 'View Only' }]);
    setDropdownOpen([...dropdownOpen, false]);
  };

  const toggleDropdown = (index) => {
    const newDropdownOpen = [...dropdownOpen]; // Create a copy of dropdownOpen state
    newDropdownOpen[index] = !newDropdownOpen[index]; // Toggle the dropdown state for the clicked share
    setDropdownOpen(newDropdownOpen); // Update the dropdown state
  };

  const toggleCheckboxDropdown = () => {
    if (groupShare) {
      setCheckboxDropdownOpen(!checkboxDropdownOpen);
    }
  };
  // Update the shareTemplate function to update the sharedAccessData prop after sharing the template
  const shareTemplate = (recipientAccessMapping) => {
    return new Promise((resolve, reject) => {
      const fetchUserSharedTemplateDetails = () => {
        return fetchUserSharedConfigsDetails(apiClient, configID)
          .then((response) => {
            setShares([
              ...response,
              {
                email: '',
                access: 'View Only',
              },
            ]);
          })
          .catch((error) => {
            console.error('Error fetching user shared configs details:', error);
            reject(error);
          });
      };

      const handleShareTemplate = () => {
        const filteredRecipientAccessMapping = {};

        for (const [email, access] of Object.entries(recipientAccessMapping)) {
          if (
            !sharedAccessData.some(
              (entry) =>
                entry.email.toLowerCase() === email.toLowerCase() &&
                entry.access === access,
            )
          ) {
            filteredRecipientAccessMapping[email] = access;
          }
        }
        const convertedRecipientAccessMapping = Object.fromEntries(
          Object.entries(filteredRecipientAccessMapping).map(
            ([email, access]) => {
              return [
                email.toLowerCase(),
                convertAccessLevelForBackend(access),
              ];
            },
          ),
        );
        apiClient
          .shareTemplate(configID, convertedRecipientAccessMapping)
          .then((response) => {
            console.log(response['invalid_emails']);
            if ('invalid_emails' in response) {
              // Send the error to the label
              setLabelText(
                Object.keys(response['invalid_emails']).join(', ') +
                  ' not found.',
              );
            } else {
              console.log('Template shared:', response);
              onClose();
            }
            // Fetch updated user/config data after sharing template
            Promise.all([fetchUserSharedTemplateDetails()])
              .then(([userConfigs, sharedConfigsDetials]) => {
                resolve(response);
              })
              .catch((error) => {
                console.error('Error sharing template:', error);
                onClose();
                reject(error);
              });
          })
          .catch((error) => {
            console.error('Error sharing template:', error);
            onClose();
            reject(error);
          });
      };

      handleShareTemplate();
    });
  };

  const handleSubmit = () => {
    // Check if sharing is already in progress, if so, return
    setLabelText('');
    if (sharingInProgress) return;
    setSharingInProgress(true);
    const recipientAccessMapping = {};
    let shouldContinue = true;

    shares.forEach((share) => {
      if (share.email.trim() !== '') {
        recipientAccessMapping[share.email] = share.access;
        if (share.email === userEmail) {
          setLabelText("Can't share template with yourself.");
          shouldContinue = false;
          return; // Stop the code execution here
        }
      }
    });
    if (!shouldContinue) {
      setSharingInProgress(false); // If sharing is not in progress, set the state back to false
      return; // Stop the code execution here
    }

    if (groupShare) {
      const domain = userEmail.split('@')[1];
      recipientAccessMapping[`@${domain}`] = lastDropdownValue;
    }
    if (Object.keys(recipientAccessMapping).length > 0) {
      shareTemplate(recipientAccessMapping).finally(() => {
        setSharingInProgress(false);
      });
    }
  };

  const resetState = () => {
    setGroupShare(false);
    setCheckboxDropdownOpen(false);
    setLastDropdownValue('View Only');
    setError(null);
  };

  const handleClose = () => {
    resetState(); // Reset state when modal is closed
    setLabelText('');
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2 className="mb-4">Share Note Setting</h2>
        <div className="text-muted mb-3">
          <small>
            <strong>Collaborate:</strong> You both edit the same note settings.<br />
            <strong>View Only:</strong> They can use your note settings, but only you can edit them.<br />
            <strong>Duplicate:</strong> They can train the AI their way. Your settings stay the same.<br />
          </small>
        </div>
        <div className="text-center text-muted mb-4">
          <small>{labelText}</small>
        </div>
        {shares.map((share, index) => (
          <div key={index} className="d-flex align-items-center mb-2">
            <Input
              type="text"
              placeholder="Add email and select permission"
              value={share.email}
              onChange={(e) =>
                handleShareChange(index, e.target.value, 'email')
              }
              className="mr-2"
            />
            <Dropdown
              isOpen={dropdownOpen[index]}
              toggle={() => toggleDropdown(index)}
            >
              <DropdownToggle caret>{share.access}</DropdownToggle>
              <DropdownMenu right={!index}>
                <DropdownItem
                  onClick={() =>
                    handleShareChange(index, 'View Only', 'access')
                  }
                >
                  <i className="fa fa-eye"></i>View Only
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    handleShareChange(index, 'Full Access', 'access')
                  }
                >
                  <i className="fa fa-edit"></i>Collaborate
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    handleShareChange(index, 'Duplicate', 'access')
                  }
                >
                  <i className="fa fa-copy"></i>Duplicate
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    handleShareChange(index, 'Remove Access', 'access')
                  }
                >
                  <i className="fa fa-eye-slash"></i>Remove Access
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            {index === shares.length - 1 && (
              <Button color="primary" onClick={addShare} className="ml-2">
                Add
              </Button>
            )}
          </div>
        ))}
        {userEmail &&
          !userEmail.endsWith('@gmail.com') &&
          !userEmail.endsWith('@yahoo.com') &&
          !userEmail.endsWith('@aol.com') &&
          !userEmail.endsWith('@corahealth.com') &&
          !userEmail.endsWith('@hotmail.com') && (
            <FormGroup
              check
              className="mb-2 d-flex align-items-center justify-content-center"
            >
              <Label check className="mr-3">
                <Input
                  type="checkbox"
                  checked={groupShare}
                  onChange={() => setGroupShare(!groupShare)}
                />
                {''}Share with @{userEmail.split('@')[1]}
              </Label>
              {groupShare && (
                <Dropdown
                  isOpen={checkboxDropdownOpen}
                  toggle={toggleCheckboxDropdown}
                  className="mr-3"
                >
                  <DropdownToggle caret>{lastDropdownValue}</DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem
                      onClick={() => setLastDropdownValue('View Only')}
                    >
                      <i className="fa fa-eye"></i>View Only
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => setLastDropdownValue('Full Access')}
                    >
                      <i className="fa fa-edit"></i>Full Access
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => setLastDropdownValue('Duplicate')}
                    >
                      <i className="fa fa-copy"></i>Duplicate
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => setLastDropdownValue('Remove Access')}
                    >
                      <i className="fa fa-eye-slash"></i>Remove Access
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              )}
            </FormGroup>
          )}

        {error && <p className="text-danger">{error}</p>}

        <div className="modal-buttons">
          <Button color="primary" onClick={handleSubmit}>
            Invite
          </Button>
          <Button color="danger" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareTemplateModal;
